import React, { useState, useEffect, useRef } from "react";
import ApiDevice from "../../../app/api/admin/device/ApiDevice";
import ApiFile from "../../../app/api/admin/file/ApiFile";
import Modal from "../../components/modals/modal";
import Button from "../../components/button";
import Select from "../../components/inputs/select";
import InputText from "../../components/inputs/text";
import InputCheckbox from "../../components/inputs/checkbox";
import InputFile from "../../components/inputs/file";
import Loader from "../../components/loader";
import FileLib from "../../../inc/FileLib";
import SportsEsportsIcon from "@mui/icons-material/SportsEsports";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import BuildIcon from "@mui/icons-material/Build";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CodeIcon from "@mui/icons-material/Code";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import LoginIcon from "@mui/icons-material/Login";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import StopIcon from "@mui/icons-material/Stop";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import VpnKeyOffIcon from "@mui/icons-material/VpnKeyOff";
import InstallMobileIcon from "@mui/icons-material/InstallMobile";
import MoveUpIcon from "@mui/icons-material/MoveUp";
import PanToolOutlinedIcon from "@mui/icons-material/PanToolOutlined";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import PasswordIcon from "@mui/icons-material/Password";
import SystemUpdateAltIcon from "@mui/icons-material/SystemUpdateAlt";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import LogoutIcon from "@mui/icons-material/Logout";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import PersonOffIcon from "@mui/icons-material/PersonOff";

// Уровни защиты (danger каталога): 1 — без окна (частые безопасные, см. needsConfirm); 2 — подтвердить
// и отметить «Понимаю последствия»; 3 — + ввести число устройств (бэкенд сверяет его же — confirm_count).
export const LEVEL_CONFIRM = 1;
export const LEVEL_AWARE = 2;
export const LEVEL_TYPE_COUNT = 3;

// Ссылка на скрипт (link каталога): null — не нужна, optional — можно, required — без неё 422.
export function isLinkRequired(action) {
    return action?.link === 'required';
}

export function isLinkAllowed(action) {
    return Boolean(action?.link);
}

// Действия панели (family = panel): тела свои, часть открывает модалку.
export const PANEL_MODAL_ACTIONS = ['transfer', 'install-apk'];

// Раскладка панели — согласована с владельцем (архитектор интерфейсов + оператор): смысловые колонки,
// частое — кнопками на виду (primary), редкое — в «Ещё ▾» (more), опасное — под красным раскрытием
// (danger). Действия берутся из каталога бэкенда; чего тут нет — попадает в «Ещё» (или в «Опасные»,
// если danger ≥ 2) колонки по группе каталога: новое действие бэкенда не потеряется.
// TODO: когда бэкенд отдаст в каталоге section / tier — раскладку брать оттуда.
export const BATCH_SECTIONS = [
    {
        key: 'mm2',
        title: 'MM2',
        icon: SportsEsportsIcon,
        groups: ['mm2'],
        primary: ['restart-mm2-job'],
        accent: 'restart-mm2-job',
        // Поле ссылки + кнопка рядом; то же поле берут остальные действия колонки со ссылкой.
        linkAction: 'restart-mm2-script',
        hasImmediate: true,
        more: ['restart-mm2-level', 'launch-mm2', 'start-level', 'launch-job', 'restart', 'graphics-low', 'launch-game'],
        danger: [],
    },
    {
        key: 'account',
        title: 'Аккаунт',
        icon: AccountCircleIcon,
        groups: ['activity', 'auth'],
        primary: ['warmup', 'auto-login'],
        accent: 'warmup',
        more: ['favorite-game', 'add-friend', 'remove-friend', 'friend-requests', 'change-avatar'],
        danger: ['restart-clear', 'clear-data', 'change-password', 'register-account'],
    },
    {
        key: 'container',
        title: 'Контейнер',
        icon: Inventory2Icon,
        groups: ['container'],
        primary: ['start', 'reboot', 'stop', 'update'],
        accent: 'start',
        labels: { start: 'Поднять', reboot: 'Перезапустить', stop: 'Остановить', update: 'Пересборка' },
        more: [],
        danger: ['reinstall', 'rebuild', 'reset', 'remove'],
    },
    {
        key: 'service',
        title: 'Обслуживание',
        shortTitle: 'Сервис',
        icon: BuildIcon,
        groups: ['network', 'manage'],
        primary: ['install-apk', 'transfer', 'proxy-clear'],
        accent: 'install-apk',
        // Пара-переключатель «Авто | Ручные» — одной строкой, по смыслу это одно действие.
        toggle: ['manual-off', 'manual-on'],
        labels: { 'manual-off': 'Авто', 'manual-on': 'Ручные' },
        more: ['net-tools', 'pcmark'],
        danger: [],
    },
];

// Каталог (GET devices/batch-actions) → {byName: {name: действие}, sections: [{...секция, primary,
// more, danger — уже действиями каталога}]}. Чего в каталоге нет — не показываем.
export function buildCatalog(groups) {
    const byName = {};
    (groups ?? []).forEach((group) => {
        group.actions.forEach((action) => { byName[action.name] = { ...action, groupKey: group.key }; });
    });
    const placed = new Set(BATCH_SECTIONS.flatMap((section) =>
        [...section.primary, ...section.more, ...section.danger, ...(section.toggle ?? []), section.linkAction].filter(Boolean)));
    const knownGroups = new Set(BATCH_SECTIONS.flatMap((section) => section.groups));
    const lastKey = BATCH_SECTIONS[BATCH_SECTIONS.length - 1].key;
    const pick = (names) => names.map((name) => byName[name]).filter(Boolean);
    const sections = BATCH_SECTIONS.map((section) => {
        // Группа каталога, которой нет в раскладке, — в последнюю колонку.
        const isOwn = (action) => section.groups.includes(action.groupKey)
            || ((section.key === lastKey) && !knownGroups.has(action.groupKey));
        const extra = Object.values(byName)
            .filter((action) => !placed.has(action.name) && isOwn(action))
            .sort((a, b) => a.order - b.order);
        return {
            ...section,
            linkAction: section.linkAction ? (byName[section.linkAction] ?? null) : null,
            primary: pick(section.primary),
            toggle: pick(section.toggle ?? []),
            more: [...pick(section.more), ...extra.filter((action) => action.danger < LEVEL_AWARE)],
            danger: [...pick(section.danger), ...extra.filter((action) => action.danger >= LEVEL_AWARE)],
        };
    });
    return { byName, sections };
}

// Нужно ли подтверждение: danger 1 — частые безопасные действия, окно на них только приучает жать
// «Да» не читая. «Сразу» может уронить запуск — тогда подтверждаем и безопасное.
export function needsConfirm(action, isImmediate) {
    return (action.danger >= LEVEL_AWARE) || isImmediate;
}

// Иконки действий (MUI). Нет в карте — кнопка без иконки, подпись с бэкенда.
export const ACTION_ICONS = {
    'restart-mm2-job': PlayArrowIcon,
    'restart-mm2-script': CodeIcon,
    'warmup': LocalFireDepartmentIcon,
    'auto-login': LoginIcon,
    'start': PowerSettingsNewIcon,
    'reboot': RestartAltIcon,
    'stop': StopIcon,
    'update': AutorenewIcon,
    'proxy-clear': VpnKeyOffIcon,
    'install-apk': InstallMobileIcon,
    'transfer': MoveUpIcon,
    // Те же значки, что метка режима в строке таблицы: робот — автоматика, рука — ручное.
    'manual-on': PanToolOutlinedIcon,
    'manual-off': SmartToyOutlinedIcon,
    'restart-clear': LogoutIcon,
    'clear-data': DeleteSweepIcon,
    'change-password': PasswordIcon,
    'register-account': PersonAddIcon,
    'reinstall': SystemUpdateAltIcon,
    'rebuild': AutorenewIcon,
    'reset': DeleteSweepIcon,
    'remove': DeleteForeverIcon,
};

// Вид кнопки — по роли, а не по «нужно ли окно»: одна заливка на колонку (главное действие),
// остальные — нейтральные; с подтверждением (danger 2) — жёлтая полоска слева и ⚠; красное — только
// под «Опасными».
export function actionButtonClass(action, { isAccent = false, isDanger = false } = {}) {
    const classes = ['devices__batch-btn'];
    if (isDanger) classes.push('devices__batch-btn_danger');
    else if (isAccent) classes.push('devices__batch-btn_accent');
    if (!isDanger && (action?.danger ?? 0) >= LEVEL_AWARE) classes.push('devices__batch-btn_confirm');
    return classes.join(' ');
}

// Кнопка действия: иконка, подпись (одна строка, многоточие), справа — подсказка-счётчик или «›» у
// действий с модалкой.
export function BatchActionButton({ action, label, className, hint, isModal, isBusy, title, ...other }) {
    const Icon = ACTION_ICONS[action.name];
    return <Button className={className} title={[title, hint?.title].filter(Boolean).join('\n')} {...other}>
        {Icon ? <Icon className="devices__batch-btn-icon"/> : null}
        <span className="devices__batch-btn-label">{isBusy ? 'Запуск…' : label}</span>
        {hint ? <span className={`devices__batch-btn-hint${hint.isWarn ? ' devices__batch-btn-hint_warn' : ''}`}>
            {hint.isWarn ? <PersonOffIcon className="devices__batch-btn-hint-icon"/> : null}
            {hint.text}
        </span> : null}
        {isModal ? <ChevronRightIcon className="devices__batch-btn-chevron"/> : null}
    </Button>;
}

// Раскрытие «Опасные» внизу колонки. Опасных нет — пустая ячейка той же высоты: нижняя линия
// колонок совпадает.
export function BatchDangerZone({ count, isOpen, onToggle, children }) {
    if (count === 0) return <div className="devices__batch-danger devices__batch-danger_empty"/>;
    return <div className={`devices__batch-danger${isOpen ? ' devices__batch-danger_open' : ''}`}>
        <button type="button" className="devices__batch-danger-toggle" onClick={onToggle}>
            <WarningAmberIcon className="devices__batch-btn-icon"/>
            <span>Опасные · {count}</span>
            {isOpen ? <ExpandLessIcon className="devices__batch-btn-icon"/> : <ExpandMoreIcon className="devices__batch-btn-icon"/>}
        </button>
        {isOpen ? <div className="devices__batch-danger-list">{children}</div> : null}
    </div>;
}

// --- Инфографика -------------------------------------------------------------------------------

// Статусы устройства → сегмент полосы: работает / в процессе / не работает.
const STATUS_GROUPS = {
    active: 'ok',
    new: 'progress', queued: 'progress', installing: 'progress', rebooting: 'progress', resetting: 'progress',
    stopped: 'bad', error: 'bad',
};

// Сводка по выбранным, по строкам, что есть на фронте (текущая страница списка). Бэкенд офлайн,
// без аккаунта и ручные НЕ пропускает — скрипт встанет и упадёт на устройстве, поэтому это
// предупреждение «на этих не сработает», а не прогноз пропусков.
export function summarizeDevices(rows) {
    const ids = (predicate) => rows.filter(predicate).map((row) => row.id);
    return {
        ok: ids((row) => STATUS_GROUPS[row.status] === 'ok'),
        progress: ids((row) => STATUS_GROUPS[row.status] === 'progress'),
        bad: ids((row) => STATUS_GROUPS[row.status] === 'bad'),
        noAccount: ids((row) => !(Number(row.external_id) > 0)),
        withProxy: ids((row) => Boolean(row.proxy_id)),
        manual: ids((row) => Boolean(row.is_manual)),
        auto: ids((row) => !row.is_manual),
        proxyError: ids((row) => row.proxy_status === 'error'),
        proxySetup: ids((row) => row.proxy_status === 'setup' || row.proxy_status === 'removing'),
        health: ids((row) => row.health_status === 'repair' || row.health_status === 'error'),
        nodes: new Set(rows.map((row) => row.node_name).filter(Boolean)).size,
    };
}

// Подсказка-счётчик у кнопки: к скольким выбранным действие применимо (или на скольких не сработает).
export function actionHint(action, summary) {
    if (!summary) return null;
    switch (action.name) {
        // isEmpty — среди выбранных применить не к чему (кнопку гасим, если сводка полная).
        case 'start':
            return { text: String(summary.bad.length), isEmpty: summary.bad.length === 0, title: `остановлены / с ошибкой: ${summary.bad.length}` };
        case 'proxy-clear':
            return { text: String(summary.withProxy.length), isEmpty: summary.withProxy.length === 0, title: `с прокси: ${summary.withProxy.length}` };
        case 'manual-on':
            return { text: String(summary.auto.length), isEmpty: summary.auto.length === 0, title: `сейчас на автоматике: ${summary.auto.length}` };
        case 'manual-off':
            return { text: String(summary.manual.length), isEmpty: summary.manual.length === 0, title: `сейчас ручные: ${summary.manual.length}` };
        case 'restart-mm2-job':
        case 'warmup':
        case 'auto-login':
            return summary.noAccount.length > 0
                ? { text: String(summary.noAccount.length), isWarn: true, title: `без аккаунта: ${summary.noAccount.length} — на них упадёт` }
                : null;
        default:
            return null;
    }
}

// Строка сводки в шапке: полоса статусов + кликабельные счётчики (клик — снять эти с выделения).
export function BatchSummary({ summary, known, total, onDeselect }) {
    const segments = [['ok', summary.ok.length, 'работают'], ['progress', summary.progress.length, 'в процессе'], ['bad', summary.bad.length, 'остановлены / ошибка']];
    const chips = [
        [summary.bad, 'остановлены', 'bad'],
        [summary.noAccount, 'без аккаунта', 'warn'],
        [summary.manual, 'ручные', 'muted'],
        [summary.proxyError, 'прокси: ошибка', 'bad'],
        [summary.proxySetup, 'прокси ставится', 'muted'],
        [summary.health, 'проблемы здоровья', 'warn'],
    ].filter(([ids]) => ids.length > 0);
    return <div className="devices__batch-summary">
        <div className="devices__batch-status-bar" role="img"
            title={`Статусы выбранных — ${segments.map(([, count, label]) => `${label}: ${count}`).join(', ')}`}
            aria-label={`Статусы выбранных — ${segments.map(([, count, label]) => `${label}: ${count}`).join(', ')}`}>
            {segments.map(([key, count]) => count > 0
                ? <span key={key} className={`devices__batch-status-bar-segment devices__batch-status-bar-segment_${key}`} style={{ flexGrow: count }}/>
                : null)}
        </div>
        {segments.map(([key, count, label]) => count > 0
            ? <span key={key} className={`devices__batch-dot devices__batch-dot_${key}`} title={label}>{count}</span>
            : null)}
        {chips.map(([ids, label, tone]) => <button type="button" key={label}
            className={`devices__batch-chip devices__batch-chip_${tone}`}
            title="Снять эти устройства с выделения" onClick={() => onDeselect(ids)}>
            {label} {ids.length} ×
        </button>)}
        {summary.nodes > 1 ? <span className="devices__batch-chip devices__batch-chip_muted">нод {summary.nodes}</span> : null}
        {known < total ? <span className="devices__batch-summary-note" title="Сводка — по устройствам на текущей странице списка">
            по {known} из {total}
        </span> : null}
    </div>;
}

// Ход запуска — одна сегментная полоса: успех, ошибка, идёт, ждёт, отменено.
export function BatchProgress({ statuses }) {
    const count = (...list) => statuses.filter((item) => list.includes(item.status)).length;
    const segments = [
        ['success', count('success')],
        ['error', count('error')],
        ['running', count('running')],
        ['pending', count('pending')],
        ['cancelled', count('cancelled')],
    ];
    const total = statuses.length;
    if (total === 0) return null;
    return <div className="devices__batch-progress" title={`успешно ${segments[0][1]}, ошибка ${segments[1][1]}, идёт ${segments[2][1]}, ждёт ${segments[3][1]}, отменено ${segments[4][1]}`}>
        {segments.map(([key, value]) => value > 0
            ? <span key={key} className={`devices__batch-progress-segment devices__batch-progress-segment_${key}`} style={{ flexGrow: value }}/>
            : null)}
    </div>;
}

// «Ещё ▾» — в строке заголовка колонки, со списком редких действий; клик по пункту запускает действие.
// Открывается вверх (панель прилипает к низу экрана), прижат к правому краю колонки.
export function BatchMoreMenu({ actions, disabled, isItemDisabled, onPick }) {
    const [_isOpen, set_isOpen] = useState(false);
    const rootRef = useRef(null);

    useEffect(() => {
        if (!_isOpen) return undefined;
        const handleDown = (event) => {
            if (rootRef.current && !rootRef.current.contains(event.target)) set_isOpen(false);
        };
        const handleKey = (event) => {
            if (event.key === 'Escape') set_isOpen(false);
        };
        document.addEventListener('mousedown', handleDown);
        document.addEventListener('keydown', handleKey);
        return () => {
            document.removeEventListener('mousedown', handleDown);
            document.removeEventListener('keydown', handleKey);
        };
    }, [_isOpen]);

    if (actions.length === 0) return null;
    return <div className="devices__batch-more" ref={rootRef}>
        <button type="button" className="devices__batch-more-toggle" disabled={disabled}
            title="Ещё действия" onClick={() => set_isOpen((open) => !open)}>
            <MoreHorizIcon className="devices__batch-btn-icon"/> +{actions.length}
        </button>
        {_isOpen ? <div className="devices__batch-more-list">
            {actions.map((action) => <button type="button" key={action.name} className="devices__batch-more-item"
                disabled={isItemDisabled?.(action)} title={action.note ?? undefined}
                onClick={() => { set_isOpen(false); onPick(action); }}>
                {action.label}
                {action.note ? <span className="devices__batch-more-note">{action.note}</span> : null}
            </button>)}
        </div> : null}
    </div>;
}

// Текст про «Сразу» — по реальному поведению (ModelScriptExecute::dispatchNow): запуск без ожидания
// тика очереди, ничего не отменяет; занято — ошибка, а не очередь.
export const IMMEDIATE_NOTE = 'Без очереди: если на устройстве уже идёт сценарий или занят общий лимит (20 одновременно), запуск упадёт с ошибкой через ~20 с, в очередь не встанет.';

// Сколько имён устройств показать в подтверждении.
const CONFIRM_NAMES_LIMIT = 5;

// Подтверждение массового действия по уровню: 1 — кнопка с глаголом; 2 — плюс «понимаю»;
// 3 — плюс ввести число устройств. Тело — что именно произойдёт и на каких устройствах.
export function BatchConfirmModal({ action, deviceNames, isImmediate, warning, onConfirm, onClose }) {
    const [_isAware, set_isAware] = useState(false);
    const [_typedCount, set_typedCount] = useState('');
    const count = deviceNames.length;
    const level = action.danger ?? LEVEL_CONFIRM;
    const isAllowed = (level < LEVEL_AWARE || _isAware) && (level < LEVEL_TYPE_COUNT || _typedCount.trim() === String(count));
    const restCount = count - CONFIRM_NAMES_LIMIT;

    return <Modal isOpenModal={true} closeModal={onClose} title={`${action.label} — ${count} устр.`}
        className="devices__batch-confirm">
        {action.note ? <p className="devices__batch-confirm-text">{action.note}.</p> : null}
        {isImmediate ? <p className="devices__batch-confirm-text devices__batch-confirm-text_warn">{IMMEDIATE_NOTE}</p> : null}
        {warning ? <p className="devices__batch-confirm-text devices__batch-confirm-text_warn">{warning}</p> : null}
        <div className="devices__batch-confirm-names">
            {deviceNames.slice(0, CONFIRM_NAMES_LIMIT).join(', ')}{restCount > 0 ? ` и ещё ${restCount}` : ''}
        </div>
        {level >= LEVEL_AWARE ? <InputCheckbox value={_isAware} onChange={(e) => set_isAware(e.target.checked)}>
            Понимаю последствия
        </InputCheckbox> : null}
        {level >= LEVEL_TYPE_COUNT ? <InputText value={_typedCount} hasLabel={true} inputMode="numeric"
            onChange={(e) => set_typedCount(e.target.value)}>{`Введите ${count}, чтобы подтвердить`}</InputText> : null}
        <div className="devices__batch-confirm-actions">
            <Button onClick={onClose}>Отмена</Button>
            <Button className={level >= LEVEL_AWARE ? 'btn-danger' : 'btn-success'} disabled={!isAllowed} onClick={onConfirm}>
                {`Запустить на ${count} устр.`}
            </Button>
        </div>
    </Modal>;
}

// Установка APK на отмеченные: с сервера (каталог «APK-файлы», файл не идёт через браузер — работает и
// для больших) или с ПК. Источник — ровно один: бэкенд запрещает apk и file_id разом.
export function BatchApkModal({ deviceIds, onClose, onSkipped }) {
    const [_storedApks, set_storedApks] = useState(null); // {id: 'имя (размер)'}; null — грузится
    const [_storedApkId, set_storedApkId] = useState('');
    const [_apkFile, set_apkFile] = useState(null);
    const [_isInstalling, set_isInstalling] = useState(false);
    const [_summary, set_summary] = useState(null);

    // Каталог APK — при открытии: он меняется в рантайме (раздел «APK-файлы»).
    useEffect(() => {
        (async () => {
            const files = await ApiFile.Index({ type_file: 'apk', per_page: 200 }).getResponse();
            const options = {};
            (files ?? []).forEach((file) => {
                // Версия (versionName) — чтобы при нескольких сборках одного приложения не ошибиться.
                options[file.id] = `${file.name}${file.version ? ` · ${file.version}` : ''} (${FileLib.formatterSize(file.size) ?? 'нет файла'})`;
            });
            set_storedApks(options);
        })();
    }, []);

    const install = async (isStored) => {
        set_isInstalling(true);
        set_summary(null);
        try {
            // Select отдаёт ключ строкой — бэкенд ждёт integer.
            const response = isStored
                ? await ApiDevice.BatchInstallStored(deviceIds, Number(_storedApkId))
                : await ApiDevice.BatchInstall(deviceIds, _apkFile);
            onSkipped(Object.entries(response?.skipped_reasons ?? {}), Boolean(response?.queued));
            const count = (response?.device_ids ?? []).length;
            set_summary(response?.queued ? `Задача создана: установка на ${count} устр. идёт в фоне` : 'Не удалось создать задачу');
        } finally {
            set_isInstalling(false);
        }
    };

    return <Modal isOpenModal={true} closeModal={_isInstalling ? () => {} : onClose}
        title={`Установка APK — ${deviceIds.length} устр.`} className="devices__batch-apk">
        <div className="devices__batch-section-title">С сервера (раздел «APK-файлы»)</div>
        {_storedApks === null
            ? <Loader className="loader_inline" text="Каталог APK…"/>
            : Object.keys(_storedApks).length === 0
            ? <span className="devices__batch-result">Нет загруженных APK</span>
            : <div className="devices__batch-row">
                <Select options={_storedApks} value={_storedApkId} hasLabel={false} isNull={true}
                    className="devices__batch-stored-select" onChange={(e) => set_storedApkId(e.target.value)}/>
                <Button className="btn-success" disabled={!_storedApkId || _isInstalling} onClick={() => install(true)}>
                    {_isInstalling ? 'Установка…' : 'Установить'}
                </Button>
            </div>}
        <div className="devices__batch-section-title">С компьютера</div>
        <div className="devices__batch-row">
            <InputFile value={_apkFile} hasLabel={false} accept=".apk"
                onChange={(e) => set_apkFile(e.target.files[0] ?? null)}/>
            <Button disabled={!_apkFile || _isInstalling} onClick={() => install(false)}>
                {_isInstalling ? 'Установка…' : 'Установить'}
            </Button>
        </div>
        {_summary ? <span className="devices__batch-result">{_summary}</span> : null}
        <div className="devices__batch-confirm-actions">
            <Button disabled={_isInstalling} onClick={onClose}>Закрыть</Button>
        </div>
    </Modal>;
}
